import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Heart, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { apiClient } from "../lib/apiClient";


export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. No token provided.");
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await apiClient.post("authResetPassword", {
        token,
        password: formData.password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-light noise-bg flex flex-col">
      <header className="p-4">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-stone-dark/70 hover:text-sage transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
            </Link>
            <h1 className="font-serif text-2xl font-semibold text-stone-dark mt-4">
              {success ? "Password reset!" : "Create new password"}
            </h1>
            <p className="text-stone-dark/60 mt-2">
              {success ? "You can now sign in" : "Enter your new password below"}
            </p>
          </div>

          <Card className="border-stone shadow-lg" data-testid="reset-password-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-serif text-xl text-center">
                {success ? "All Set" : "New Password"}
              </CardTitle>
              <CardDescription className="text-center">
                {success ? "Your password has been updated" : "Choose a strong password for your account"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {success ? (
                <div className="text-center py-4" data-testid="reset-password-success">
                  <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-sage" />
                  </div>
                  <p className="text-stone-dark/60 text-sm mb-6">
                    Your password has been reset successfully. You can now sign in with your new password.
                  </p>
                  <Button
                    className="w-full bg-sage hover:bg-sage-dark text-white rounded-full py-5"
                    onClick={() => navigate("/login")}
                    data-testid="go-to-login-btn"
                  >
                    Sign In
                  </Button>
                </div>
              ) : !token ? (
                <div className="text-center py-4" data-testid="reset-password-invalid">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-stone-dark/60 text-sm mb-6">
                    This reset link is invalid. Please request a new password reset.
                  </p>
                  <Link to="/forgot-password">
                    <Button className="w-full bg-sage hover:bg-sage-dark text-white rounded-full py-5" data-testid="request-new-reset">
                      Request New Reset Link
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="reset-password-error">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="At least 6 characters"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20 pr-10"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/50 hover:text-stone-dark"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirm ? "text" : "password"}
                        placeholder="Re-enter your new password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        minLength={6}
                        className="bg-stone-light border-stone focus:border-sage focus:ring-sage/20 pr-10"
                        data-testid="reset-password-confirm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/50 hover:text-stone-dark"
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-sage hover:bg-sage-dark text-white rounded-full py-5 mt-6 font-medium"
                    disabled={loading}
                    data-testid="reset-password-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </form>
              )}

              <div className="mt-6 text-center">
                <p className="text-stone-dark/60 text-sm">
                  Remember your password?{" "}
                  <Link to="/login" className="text-sage hover:underline font-medium" data-testid="login-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sage/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>
    </div>
  );
}
